import React, { useMemo, useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import { Form } from "@unform/web";

// BOOTSTRAP
import Row from "~/components/Bootstrap/Row";
import Hr from "~/components/Bootstrap/Hr";
import { ColSm12, ColSm2, ColSm3, ColSm4, ColSm5, ColSm6, ColSm8 } from "~/components/Bootstrap/Col";
import Label from "~/components/Bootstrap/Label";
import SubLabel from "~/components/Bootstrap/SubLabel";

// COMPONENTES GERAIS
import Modal, { ModalHeader } from "~/components/Modal";

// STYLED COMPONENTS
import { Main, Content, Footer } from "./styles";

// INPUTS
import InputFormNormal from "~/components/Inputs/Form/Normal";
import SelectFormNormal from "~/components/Selects/Form/Normal";

// BUTONS
import ButtonPrimary from "~/components/Buttons/Normal/Primary";
import ButtonSecondary from "~/components/Buttons/Normal/Secondary";

// UTILS
import Utils from "~/utils";
import NotificacaoUtil from "~/utils/notificacao";

// SERVICES
import APIRequests from "~/services/requests/painel";

const ModalCadastroEdicao = forwardRef(({ onConfirmCadEdit = () => {} }, ref) => {
    /**
     * Cria a referencia do formulário.
     */
    const FormREF = useRef(null);

    /**
     * Dados iniciais do form.
     */
    const [FormInitialData, setFormInitialData] = useState({});

    const [isVisible, setVisible] = useState(false);

    const [UUID, setUUID] = useState("");

    const onRequestConfirm = async () => {
        FormREF?.current?.submitForm();
    };

    const onRequestSearchCNPJ = async () => {
        try {
            /**
             * Mantém somente os números
             */
            const cnpj = Utils.SomenteNumeros(FormREF?.current?.getFieldValue("cpf_cnpj"));

            if (!cnpj) {
                throw new Error("Informe o CNPJ");
            }

            /**
             * Consulta no lambda.
             */
            const data = await APIRequests.lambda.cnpj(cnpj);

            /**
             * Atualiza no form
             */
            FormREF?.current.setData({
                nome: data?.nome || "",
                fantasia: data?.fantasia || "",

                ie_rg: data?.ie_rg || "",

                complemento: data?.complemento || "",
                logradouro: data?.logradouro || "",
                municipio: data?.municipio || "",
                numero: data?.numero || "",
                bairro: data?.bairro || "",
                cep: data?.cep || "",
                uf: data?.uf || "",
            });
        } catch (error) {
            NotificacaoUtil.error({
                msg: error?.message?.replaceAll(`\n`, "<br />") || "",
                timeout: 3500,
            });
        }
    };

    const onRequestSearchCEP = async () => {
        try {
            /**
             * Mantém somente os números
             */
            const cep = Utils.SomenteNumeros(FormREF?.current?.getFieldValue("cep"));

            if (!cep) {
                throw new Error("Informe o CEP");
            }

            /**
             * Consulta no lambda.
             */
            const data = await APIRequests.lambda.cep(cep);

            /**
             * Atualiza no form
             */
            FormREF?.current.setData({ uf: data?.uf || "" });
            FormREF?.current.setData({ municipio: data?.municipio || "" });

            if (data?.logradouro) {
                FormREF?.current.setData({ logradouro: data?.logradouro || "" });
            }

            if (data?.complemento) {
                FormREF?.current.setData({ complemento: data?.complemento || "" });
            }

            if (data?.bairro) {
                FormREF?.current.setData({ bairro: data?.bairro || "" });
            }
        } catch (error) {
            NotificacaoUtil.error({
                msg: error?.message?.replaceAll(`\n`, "<br />") || "",
                timeout: 3500,
            });
        }
    };

    const onSubmit = async (data) => {
        try {
            data = {
                ...data,
                /**
                 * No novo cadastro, não estava selecionando false por padrão, então fizemos assim para cadastrar. Depois, funciona normal a edição.
                 */
                self_checkout: data.self_checkout == "true",
                // pega o cnpj somente em numeros
                cpf_cnpj_n: Utils.SomenteNumeros(data.cpf_cnpj),
            };

            let retorno_api = {};

            if (UUID) {
                retorno_api = await APIRequests.emitentes.atualizar(UUID, data);
            } else {
                retorno_api = await APIRequests.emitentes.adicionar(data);
            }

            if (retorno_api?.id) {
                NotificacaoUtil.success({
                    msg: UUID ? `Emitente atualizado com sucesso` : "Emitente cadastrado com sucesso",
                });

                // fecha o modal
                onRequestClose();

                // dispara a função externa
                onConfirmCadEdit();
            } else {
                NotificacaoUtil.error({
                    msg: UUID ? `Erro ao atualizar o emitente` : `Erro ao cadastrar o emitente`,
                    timeout: 3500,
                });
            }
        } catch (error) {
            NotificacaoUtil.error({
                msg: error?.message?.replaceAll(`\n`, "<br />") || "",
                timeout: 3500,
            });
        }
    };

    const onRequestOpen = async (uuid = false) => {
        let visible = true;

        if (uuid) {
            const dados_emitente = await APIRequests.emitentes.obter(uuid);

            if (dados_emitente.uuid) {
                /**
                 * Armazena o UUID.
                 */
                setUUID(dados_emitente.uuid);

                /**
                 * Carrega os dados adicionais
                 */
                setFormInitialData(dados_emitente);
            } else {
                visible = false;
                new Noty({
                    type: "error",
                    timeout: 2500,
                    text: "Falha ao carregar os dados do emitente.",
                }).show();
            }
        }

        if (visible) {
            setVisible(visible);
        }
    };

    const onRequestClose = () => {
        setVisible(false);

        // reseta o estado
        setUUID("");
        setFormInitialData({});
    };

    const onKeyDown = (e) => {
        // pega o código pressionado
        const code = e.which !== false ? e.which : e.keyCode;

        // stop events
        if (code == 13 || code == 27) {
            e.preventDefault();
        }

        if (code == 13) {
            onRequestConfirm();
        }

        if (code == 27) {
            onRequestClose();
        }
    };

    /**
     * Carrega os dados no form
     */
    useEffect(() => {
        if (FormREF?.current) {
            if (FormInitialData?.uuid) {
                FormREF?.current.setData(FormInitialData);
            } else {
                FormREF?.current.reset();
            }
        }
    }, [isVisible, FormREF, FormInitialData]);

    /**
     * Passa a função de buscar para fora do input via ref.
     */
    useImperativeHandle(ref, () => ({
        open: onRequestOpen,
        close: onRequestClose,
    }));

    return (
        <Modal isVisible={isVisible} setVisible={onRequestClose} closeButtonVisible={false} closeOnClickOutside={false} width={900} height={690}>
            <Main>
                <ModalHeader>{UUID ? "Edição" : "Cadastro"} de Emitente</ModalHeader>

                <Content>
                    <Form ref={FormREF} onSubmit={onSubmit}>
                        <Row>
                            <ColSm4>
                                <Label>
                                    CPF/CNPJ *
                                    <SubLabel style={{ cursor: "pointer" }} onClick={onRequestSearchCNPJ}>
                                        <i className="fad fa-search"></i> Buscar
                                    </SubLabel>
                                </Label>
                                <InputFormNormal autoFocus name="cpf_cnpj" mask="cpf_cnpj" onKeyDown={onKeyDown} />
                            </ColSm4>

                            <ColSm8>
                                <Label>Nome / Razão Social *</Label>
                                <InputFormNormal name="nome" onKeyDown={onKeyDown} />
                            </ColSm8>
                        </Row>

                        <Row>
                            <ColSm4>
                                <Label>IE/RG</Label>
                                <InputFormNormal name="ie_rg" onKeyDown={onKeyDown} />
                            </ColSm4>

                            <ColSm5>
                                <Label>Fantasia</Label>
                                <InputFormNormal name="fantasia" onKeyDown={onKeyDown} />
                            </ColSm5>

                            <ColSm3>
                                <Label>Utiliza Self Checkout</Label>
                                <SelectFormNormal name="self_checkout" onKeyDown={onKeyDown}>
                                    <option value={false}>Não</option>
                                    <option value={true}>Sim</option>
                                </SelectFormNormal>
                            </ColSm3>
                        </Row>

                        <br />
                        <Hr />
                        <br />

                        <Row>
                            <ColSm4>
                                <Label>
                                    CEP *
                                    <SubLabel style={{ cursor: "pointer" }} onClick={onRequestSearchCEP}>
                                        <i className="fad fa-search"></i> Buscar
                                    </SubLabel>
                                </Label>
                                <InputFormNormal name="cep" mask="cep" onKeyDown={onKeyDown} />
                            </ColSm4>
                            <ColSm4>
                                <Label>UF *</Label>
                                <SelectFormNormal name="uf" onKeyDown={onKeyDown}>
                                    <option value=""></option>
                                    <option value="AC">Acre</option>
                                    <option value="AL">Alagoas</option>
                                    <option value="AP">Amapá</option>
                                    <option value="AM">Amazonas</option>
                                    <option value="BA">Bahia</option>
                                    <option value="CE">Ceará</option>
                                    <option value="DF">Distrito Federal</option>
                                    <option value="ES">Espírito Santo</option>
                                    <option value="GO">Goiás</option>
                                    <option value="MA">Maranhão</option>
                                    <option value="MT">Mato Grosso</option>
                                    <option value="MS">Mato Grosso do Sul</option>
                                    <option value="MG">Minas Gerais</option>
                                    <option value="PA">Pará</option>
                                    <option value="PB">Paraíba</option>
                                    <option value="PR">Paraná</option>
                                    <option value="PE">Pernambuco</option>
                                    <option value="PI">Piauí</option>
                                    <option value="RJ">Rio de Janeiro</option>
                                    <option value="RN">Rio Grande do Norte</option>
                                    <option value="RS">Rio Grande do Sul</option>
                                    <option value="RO">Rondônia</option>
                                    <option value="RR">Roraima</option>
                                    <option value="SC">Santa Catarina</option>
                                    <option value="SP">São Paulo</option>
                                    <option value="SE">Sergipe</option>
                                    <option value="TO">Tocantins</option>
                                    <option value="EX">Estrangeiro</option>
                                </SelectFormNormal>
                            </ColSm4>

                            <ColSm4>
                                <Label>Município *</Label>
                                <InputFormNormal name="municipio" onKeyDown={onKeyDown} />
                            </ColSm4>
                        </Row>

                        <br />
                        <Hr />
                        <br />

                        <Row>
                            <ColSm4>
                                <Label>Logradouro *</Label>
                                <InputFormNormal name="logradouro" onKeyDown={onKeyDown} />
                            </ColSm4>

                            <ColSm2>
                                <Label>Número *</Label>
                                <InputFormNormal name="numero" onKeyDown={onKeyDown} />
                            </ColSm2>

                            <ColSm3>
                                <Label>Bairro *</Label>
                                <InputFormNormal name="bairro" onKeyDown={onKeyDown} />
                            </ColSm3>

                            <ColSm3>
                                <Label>Complemento</Label>
                                <InputFormNormal name="complemento" onKeyDown={onKeyDown} />
                            </ColSm3>
                        </Row>

                        <br />
                        <Hr />
                        <br />

                        <Row>
                            <ColSm4>
                                <Label>Telefone</Label>
                                <InputFormNormal name="telefone" mask="phone" onKeyDown={onKeyDown} />
                            </ColSm4>

                            <ColSm4>
                                <Label>Celular</Label>
                                <InputFormNormal name="celular" mask="phone" onKeyDown={onKeyDown} />
                            </ColSm4>

                            <ColSm4>
                                <Label>Fax</Label>
                                <InputFormNormal name="fax" mask="phone" onKeyDown={onKeyDown} />
                            </ColSm4>
                        </Row>

                        <Footer>
                            <ButtonSecondary type="button" onClick={onRequestClose}>
                                Fechar
                            </ButtonSecondary>

                            <ButtonPrimary type="submit">OK</ButtonPrimary>
                        </Footer>
                    </Form>
                </Content>
            </Main>
        </Modal>
    );
});

export default ModalCadastroEdicao;
